import type { PillVariant } from '@/components/common/Pill';

import { FlagSeverity, MiniTortStage, PrerequisiteStatus } from './types';

interface StageConfig {
  label: string;
  variant: PillVariant;
  shortLabel: string;
}

export const STAGE_CONFIG: Record<MiniTortStage, StageConfig> = {
  [MiniTortStage.PROCESSING_SUBMISSION]: {
    label: 'Processing Submission',
    shortLabel: 'Processing',
    variant: 'purple',
  },
  [MiniTortStage.REVIEWING_CLAIM]: {
    label: 'Reviewing Claim',
    shortLabel: 'Review',
    variant: 'purple',
  },
  [MiniTortStage.NEED_ADDITIONAL_MATERIALS]: {
    label: 'Need Additional Materials',
    shortLabel: 'Materials',
    variant: 'orange',
  },
  [MiniTortStage.RETRIEVING_POLICE_REPORT]: {
    label: 'Retrieving Police Report',
    shortLabel: 'Police Report',
    variant: 'orange',
  },
  [MiniTortStage.GENERATING_DEMAND]: {
    label: 'Generating Demand',
    shortLabel: 'Demand',
    variant: 'yellow',
  },
  [MiniTortStage.DEMAND_SUBMITTED]: {
    label: 'Demand Submitted',
    shortLabel: 'Submitted',
    variant: 'yellow',
  },
  [MiniTortStage.INSURER_ACKNOWLEDGED]: {
    label: 'Insurer Acknowledged',
    shortLabel: 'Acknowledged',
    variant: 'green',
  },
  [MiniTortStage.PAYMENT_ISSUED]: {
    label: 'Payment Issued',
    shortLabel: 'Payment',
    variant: 'green',
  },
  [MiniTortStage.PAYMENT_EN_ROUTE]: {
    label: 'Payment En Route',
    shortLabel: 'En Route',
    variant: 'green',
  },
  [MiniTortStage.CLOSED]: {
    label: 'Closed',
    shortLabel: 'Closed',
    variant: 'gray',
  },
  [MiniTortStage.REJECTED]: {
    label: 'Rejected',
    shortLabel: 'Rejected',
    variant: 'red',
  },
};

export const PREREQUISITE_LABELS: Record<string, string> = {
  processing_status: 'Processing',
  review_status: 'Review',
  coverage_1p_status: '1P Coverage',
  coverage_3p_status: '3P Coverage',
  police_report_status: 'Police Report',
};

export function getPrerequisiteVariant(status: PrerequisiteStatus): PillVariant {
  switch (status) {
    case PrerequisiteStatus.EXTRACTED:
    case PrerequisiteStatus.PROCESSED:
    case PrerequisiteStatus.COMPLETE:
    case PrerequisiteStatus.APPROVED:
    case PrerequisiteStatus.MANUAL:
    case PrerequisiteStatus.WAIVED:
      return 'green';
    case PrerequisiteStatus.FAILED:
      return 'red';
    case PrerequisiteStatus.PENDING:
    default:
      return 'gray';
  }
}

export function getFlagSeverityVariant(severity: FlagSeverity): PillVariant {
  switch (severity) {
    case FlagSeverity.BLOCKER:
      return 'red';
    case FlagSeverity.WARNING:
      return 'orange';
    case FlagSeverity.INFO:
    default:
      return 'gray';
  }
}

/** Valid transitions from each stage for the action buttons */
export const STAGE_TRANSITIONS: Partial<
  Record<MiniTortStage, { label: string; target: MiniTortStage; requiresInput?: 'missing_item_prompt' | 'rejection_reason'; variant?: 'destructive' | 'default' }[]>
> = {
  [MiniTortStage.REVIEWING_CLAIM]: [
    { label: 'Request Materials', target: MiniTortStage.NEED_ADDITIONAL_MATERIALS, requiresInput: 'missing_item_prompt' },
    { label: 'Reject', target: MiniTortStage.REJECTED, requiresInput: 'rejection_reason', variant: 'destructive' },
  ],
  [MiniTortStage.NEED_ADDITIONAL_MATERIALS]: [
    { label: 'Materials Received', target: MiniTortStage.REVIEWING_CLAIM },
    { label: 'Reject', target: MiniTortStage.REJECTED, requiresInput: 'rejection_reason', variant: 'destructive' },
  ],
  [MiniTortStage.RETRIEVING_POLICE_REPORT]: [
    { label: 'Reject', target: MiniTortStage.REJECTED, requiresInput: 'rejection_reason', variant: 'destructive' },
  ],
  [MiniTortStage.DEMAND_SUBMITTED]: [
    { label: 'Insurer Acknowledged', target: MiniTortStage.INSURER_ACKNOWLEDGED },
    { label: 'Payment Issued', target: MiniTortStage.PAYMENT_ISSUED },
  ],
  [MiniTortStage.INSURER_ACKNOWLEDGED]: [
    { label: 'Payment Issued', target: MiniTortStage.PAYMENT_ISSUED },
  ],
  [MiniTortStage.PAYMENT_ISSUED]: [
    { label: 'Check Mailed', target: MiniTortStage.PAYMENT_EN_ROUTE },
  ],
  [MiniTortStage.PAYMENT_EN_ROUTE]: [
    { label: 'Close Claim', target: MiniTortStage.CLOSED },
  ],
};
